import { ref, computed, watch, readonly } from 'vue';
import { useAuthentication } from '@/composables/useAuthentication';
import { useSymbolInformation } from '@/composables/useSymbolInformation';
import { buildUrlString } from '@/utils/buildUrlString';
import { loadExternalCodeFile } from '@/utils/loadExternalCodeFile';
import { wait } from '@/utils/wait';
import { waitForDomToRepaint } from '@/utils/waitForDomToRepaint';
import { OPTIONS_PLAY_CONTAINER_ID } from '@/enums/html-ids';

const OPTIONS_PLAY_WIDGET_WIDTH_PIXELS = '785px';

let hasLoadedAndExecutedOptionsPlayScript = false;
const hasSuccessfullyInitializedOptionsPlayWidget = ref(false);
const hasFailedToInitializeOptionsPlayWidget = ref(false);
const loadOptionsPlayScriptPromise = ref<Promise<void> | null>(null);
const optionsPlayWidgetSymbol = ref('');
const optionsPlayWidgetCompanyName = ref('');
let hasInitializedOptionsPlayWidgetCompanyName = false;
const isAttemptingToInitializeOptionsPlayWidget = computed(() => {
  return !!loadOptionsPlayScriptPromise.value;
});

async function loadAndExecuteOptionsPlayJavaScriptFile(): Promise<void> {
  if (hasLoadedAndExecutedOptionsPlayScript) {
    return;
  }

  const optionsPlayScriptUrl = buildUrlString('/widgetloader', { domain: 'api.optionsplay.com' });

  window.sc3WorkbenchLogger.info(`Loading OptionsPlay script with URL: ${optionsPlayScriptUrl}...`);

  await wait(500);

  // load the OptionsPlay script into the DOM
  await loadExternalCodeFile(optionsPlayScriptUrl, 3, 'js');

  window.sc3WorkbenchLogger.info(`Successfully loaded OptionsPlay script.`);

  // wait for the OptionsPlay script to execute
  await wait(0);
  await wait(0);
  await waitForDomToRepaint();
  await waitForDomToRepaint();

  hasLoadedAndExecutedOptionsPlayScript = true;
}

async function _initializeOptionsPlayWidget(): Promise<void> {
  const { hasOptionsPlayAddOn, chartCode } = useAuthentication();

  if (hasSuccessfullyInitializedOptionsPlayWidget.value) {
    return;
  }

  let userId = chartCode.value;

  if (hasOptionsPlayAddOn.value) {
    userId += '-paid';
  } else {
    userId += '-demo';
  }

  try {
    await loadAndExecuteOptionsPlayJavaScriptFile();

    let symbol = optionsPlayWidgetSymbol.value;

    const optionsPlayWidgetConfiguration = {
      destinationId: OPTIONS_PLAY_CONTAINER_ID,
      apiKey: 'e745030b943145269440ec4015ee1b7a',
      width: OPTIONS_PLAY_WIDGET_WIDTH_PIXELS,
      height: '738px',
      theme: 'light',
      showHeader: false,
      showMarketToneInHeader: true,
      hideLocalizationOptions: true,
      userId,
      symbol,
      loadPreviousSymbol: false
    };

    window.sc3WorkbenchLogger.info(`Initializing OptionsPlay widget with symbol: ${symbol}...`);

    const optionsPlayGlobalWidget = window.optionsPlayWidgetLoader.load('prostrategies', optionsPlayWidgetConfiguration, function (widget: any) {
      widget.on(widget.EventType.SYMBOL_CHANGED, function(newSymbol: string) {
        symbol = newSymbol;
      });

      widget.postMessage(widget.EventType.SET_SYMBOL, symbol);
    });

    watch(optionsPlayWidgetSymbol, () => {
      window.sc3WorkbenchLogger.info(`Setting OptionsPlay widget symbol to: ${optionsPlayWidgetSymbol.value}...`);

      optionsPlayGlobalWidget.postMessage(optionsPlayGlobalWidget.EventType.SET_SYMBOL, { symbol: optionsPlayWidgetSymbol.value, sentiment: 'Bullish' });
    });

    window.sc3WorkbenchLogger.info(`Successfully initialized OptionsPlay widget.`);

    await wait(0);
    await waitForDomToRepaint();

    hasSuccessfullyInitializedOptionsPlayWidget.value = true;
    hasFailedToInitializeOptionsPlayWidget.value = false;
  } catch (e) {
    window.sc3WorkbenchLogger.error('Error occurred while initializing the OptionsPlay widget.', e);

    hasSuccessfullyInitializedOptionsPlayWidget.value = false;
    hasFailedToInitializeOptionsPlayWidget.value = true;
  }
}

async function initializeOptionsPlayWidget(): Promise<void> {
  let promiseObject;

  if (loadOptionsPlayScriptPromise.value) {
    // there's already a request to load the OptionsPlay script underway - await the current
    // request's promise rather than kicking off a new request
    promiseObject = loadOptionsPlayScriptPromise.value;
  } else {
    promiseObject = _initializeOptionsPlayWidget();

    // store promise so that additional initializeOptionsPlayWidget function calls that are
    // made while the current network call is still underway can await this promise
    // rather than inefficiently making their own duplicate network requests for
    // the same purpose
    loadOptionsPlayScriptPromise.value = promiseObject;

    promiseObject.then(() => {
      // remove the now-resolved promise from the cache
      loadOptionsPlayScriptPromise.value = null;
    });
  }

  return await promiseObject;
}

async function setOptionsPlayWidgetSymbol(symbol: string): Promise<void> {
  const { hasOptionsPlayAddOn, isLoggedIn } = useAuthentication();

  if (!isLoggedIn.value) {
    return;
  }

  optionsPlayWidgetSymbol.value = hasOptionsPlayAddOn.value ? symbol : 'AAPL';

  await initializeOptionsPlayWidget();
}

function initializeOptionsPlayWidgetCompanyName() {
  const { getSymbolInformation } = useSymbolInformation();

  if (hasInitializedOptionsPlayWidgetCompanyName) {
    return;
  }

  watch(optionsPlayWidgetSymbol, async () => {
    const symbol = optionsPlayWidgetSymbol.value;
    optionsPlayWidgetCompanyName.value = '';

    if (!symbol) {
      return;
    }

    try {
      const symbolInformation = await getSymbolInformation(symbol);

      if (symbol !== optionsPlayWidgetSymbol.value) {
        return;
      }

      optionsPlayWidgetCompanyName.value = symbolInformation.name;
    } catch (e) {
      window.sc3WorkbenchLogger.debug(`Error occurred while getting symbol information for symbol: ${symbol} to populate optionsPlayWidgetCompanyName in useOptionsPlay composable.`, e);
    }
  });

  hasInitializedOptionsPlayWidgetCompanyName = true;
}

export function useOptionsPlay() {
  initializeOptionsPlayWidgetCompanyName();

  return {
    OPTIONS_PLAY_WIDGET_WIDTH_PIXELS,
    hasSuccessfullyInitializedOptionsPlayWidget: readonly(hasSuccessfullyInitializedOptionsPlayWidget),
    hasFailedToInitializeOptionsPlayWidget: readonly(hasFailedToInitializeOptionsPlayWidget),
    isAttemptingToInitializeOptionsPlayWidget,
    optionsPlayWidgetSymbol: readonly(optionsPlayWidgetSymbol),
    optionsPlayWidgetCompanyName: readonly(optionsPlayWidgetCompanyName),
    setOptionsPlayWidgetSymbol
  };
}
